import type { Contact } from 'types/list'
import type {
  ExternalIdentifier,
  Label,
  NamedIdWithIdentifiers,
  Pro,
  Publisher,
  Rightsholder,
} from 'types/writer'
import api from '@disco/api/server'
import {
  CONTACTS_CREATE,
  CONTACTS_CREATE_FAIL,
  CONTACTS_CREATE_SUCCESS,
  CONTACTS_UPDATE,
  CONTACTS_UPDATE_FAIL,
  CONTACTS_UPDATE_SUCCESS,
  LABEL_ADD_SUCCESS,
  LABEL_UPDATE_SUCCESS,
  PRO_ADD_SUCCESS,
  PRO_UPDATE_SUCCESS,
  PUBLISHERS_ADD_SUCCESS,
  PUBLISHERS_UPDATE_SUCCESS,
  RIGHTSHOLDER_ADD_SUCCESS,
  RIGHTSHOLDER_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'

/**
 * Adds pros to the state after successful creation.
 * @param pros - Array of pro objects to add
 * @returns Action object with pros array
 */
export const addPro = (
  pros: Pro[],
): {
  pros: Array<Pro>
  type: string
} => ({
  type: PRO_ADD_SUCCESS,
  pros,
})

export const addLabel = (
  labels: Label[],
): {
  labels: Array<Label>
  type: string
} => ({
  type: LABEL_ADD_SUCCESS,
  labels,
})

export const addRightsholder = (
  rightsholders: Rightsholder[],
): {
  rightsholders: Array<Rightsholder>
  type: string
} => ({
  type: RIGHTSHOLDER_ADD_SUCCESS,
  rightsholders,
})

/**
 * Adds publishers to the state after successful creation.
 * @param publishers - Array of publisher objects to add
 * @returns Action object with publishers array
 */
export const addPublishers = (
  publishers: Publisher[],
): {
  publishers: Array<Publisher>
  type: string
} => ({
  type: PUBLISHERS_ADD_SUCCESS,
  publishers,
})

/**
 * Updates a PRO's information in the state.
 * @param pro - PRO object with updated information
 * @returns Action object with pro data
 */
export const updatePro = (
  pro: Pro,
): {
  pro: Pro
  type: string
} => ({
  type: PRO_UPDATE_SUCCESS,
  pro,
})

/**
 * Updates a publisher's information in the state.
 * @param publisher - Publisher object with updated information
 * @returns Action object with publisher data
 */
export const updatePublisher = (
  publisher: Publisher,
): {
  publisher: Publisher
  type: string
} => ({
  type: PUBLISHERS_UPDATE_SUCCESS,
  publisher,
})

/**
 * Creates a new writer contact.
 * @param name - Name of the writer to create
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that creates a writer contact with 'Writer' tag
 */
export const createWriter = (
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<Contact>) => {
  return async (dispatch: (...args: Array<any>) => any): Promise<Contact> => {
    dispatch({
      type: CONTACTS_CREATE,
    })

    try {
      const result = await api.importContacts(
        [
          {
            name,
            tags: ['Writer'],
            ...(externalIdentifiers !== undefined && {
              external_identifiers: externalIdentifiers,
            }),
          },
        ],
        false,
      )
      const createdWriter = result.contacts[0]
      dispatch({
        type: CONTACTS_CREATE_SUCCESS,
        contact: createdWriter,
      })
      pushUINotification({
        message: 'Writer created successfully',
        level: 'success',
      })
      return createdWriter
    } catch (error) {
      dispatch({
        type: CONTACTS_CREATE_FAIL,
        error,
      })
      pushUINotification({
        message: 'Failed to create writer. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}

/**
 * Updates an existing writer contact.
 * @param id - ID of the writer contact to update
 * @param name - New name for the writer
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that updates the writer contact
 */
export const updateWriter = (
  id: number,
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<Contact>) => {
  return async (dispatch: (...args: Array<any>) => any): Promise<Contact> => {
    dispatch({
      type: CONTACTS_UPDATE,
    })

    try {
      const updatedContact: NamedIdWithIdentifiers = {
        id,
        name,
      }
      if (externalIdentifiers !== undefined) {
        updatedContact.external_identifiers = externalIdentifiers
      }
      await api.updateContacts([updatedContact])
      dispatch({
        type: CONTACTS_UPDATE_SUCCESS,
        contacts: [updatedContact],
      })
      pushUINotification({
        message: 'Writer updated successfully',
        level: 'success',
      })
      return updatedContact as Contact
    } catch (error) {
      dispatch({
        type: CONTACTS_UPDATE_FAIL,
        error,
      })
      pushUINotification({
        message: 'Failed to update writer. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}

/**
 * Creates a new publisher.
 * @param name - Name of the publisher to create
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that creates a publisher and adds it to state, returns the created publisher
 */
export const createPublisher = (
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<Publisher>) => {
  return async (dispatch: (...args: Array<any>) => any): Promise<Publisher> => {
    try {
      const result = await api.addPublisher(name, externalIdentifiers)
      dispatch(addPublishers([result]))
      pushUINotification({
        message: 'Publisher created successfully',
        level: 'success',
      })
      return result
    } catch (error) {
      pushUINotification({
        message: 'Failed to create publisher. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}

/**
 * Updates an existing publisher.
 * @param id - ID of the publisher to update
 * @param name - New name for the publisher
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that updates the publisher and updates state
 */
export const editPublisher = (
  id: number,
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      const result = await api.updatePublisher(id, name, externalIdentifiers)
      dispatch(updatePublisher(result))
      pushUINotification({
        message: 'Publisher updated successfully',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: 'Failed to update publisher. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}

/**
 * Creates a new PRO (Performance Rights Organization).
 * @param name - Name of the PRO to create
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that creates a PRO and adds it to state, returns the created PRO
 */
export const createPro = (
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<Pro>) => {
  return async (dispatch: (...args: Array<any>) => any): Promise<Pro> => {
    try {
      const result = await api.addPro(name, externalIdentifiers)
      dispatch(addPro([result]))
      pushUINotification({
        message: 'PRO created successfully',
        level: 'success',
      })
      return result
    } catch (error) {
      pushUINotification({
        message: 'Failed to create PRO. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}

/**
 * Updates an existing PRO (Performance Rights Organization).
 * @param id - ID of the PRO to update
 * @param name - New name for the PRO
 * @param externalIdentifiers - Optional external identifiers (IPI, ISNI)
 * @returns Thunk that updates the PRO and updates state
 */
export const editPro = (
  id: number,
  name: string,
  externalIdentifiers?: ExternalIdentifier[],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      const result = await api.updatePro(id, name, externalIdentifiers)
      dispatch(updatePro(result))
      pushUINotification({
        message: 'PRO updated successfully',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: 'Failed to update PRO. Please try again.',
        level: 'error',
      })
      throw error
    }
  }
}
